'use client';

/**
 * Market-aware drop-ins for next/link and next/navigation on the storefront. Internal paths are
 * written the app's way ('/koszyk', '/produkty/x') and come out in the visitor's market
 * ('/en/cart', '/en/products/x'); usePathname() returns the app's path back (prefix stripped,
 * localized route name mapped to the app route) so comparisons like `pathname === '/koszyk'` keep
 * working on every market.
 */
import React, { forwardRef, useMemo } from 'react';
import NextLink from 'next/link';
import { usePathname as useNextPathname, useRouter as useNextRouter } from 'next/navigation';
import { useOptionalTranslation } from '@/core/components/providers/TranslationProvider';
import { isLocalizableHref, localizeHtmlLinks, stripMarketPrefix } from './links';
import { toInternalRoute } from './routes';

/** Localizes an internal href for the current page's market; anything else passes through. */
export function useLocalizeHref(): (href: string) => string {
  const translation = useOptionalTranslation();
  const tPath = translation?.tPath;
  return useMemo(
    () => (href: string) => (tPath && typeof href === 'string' && isLocalizableHref(href) ? tPath(href) : href),
    [tPath],
  );
}

/**
 * Localizes internal links inside editor HTML (rich text, descriptions) for the storefront market.
 * Identity outside a storefront market — the page editor saves contentEditable innerHTML, and
 * rewritten links must never end up in a block's stored content.
 */
export function useLocalizeHtml(): (html: string) => string {
  const translation = useOptionalTranslation();
  const market = translation?.market;
  const locale = translation?.locale;
  return useMemo(
    () => (html: string) => (market && locale ? localizeHtmlLinks(html, { locale, market }) : html),
    [market, locale],
  );
}

type LinkProps = React.ComponentProps<typeof NextLink>;

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function LocaleLink({ href, ...rest }, ref) {
  const localize = useLocalizeHref();
  const localizedHref = typeof href === 'string'
    ? localize(href)
    : href && typeof href === 'object' && typeof href.pathname === 'string'
      ? { ...href, pathname: localize(href.pathname) }
      : href;
  return <NextLink ref={ref} href={localizedHref} {...rest} />;
});

export default Link;

/**
 * A Link that prefetches on intent (hover, touch, keyboard focus) instead of on entering the
 * viewport — for the dozens of product cards on a listing. Viewport prefetch fired two RSC requests
 * per visible card while the page was still loading (40+ on a category page, each running the store
 * and product layouts on the server), in the same Next process that resizes the page's images, so
 * the photos queued behind them (~1 s per prefetch measured on paputypupila). `prefetch={false}`
 * alone would also drop Next's own hover prefetch, hence the handlers.
 */
export const IntentLink = forwardRef<HTMLAnchorElement, LinkProps>(function IntentLink(
  { href, onMouseEnter, onTouchStart, onFocus, ...rest },
  ref,
) {
  const router = useRouter();
  const prefetchedRef = React.useRef(false);
  const prefetch = () => {
    if (prefetchedRef.current || typeof href !== 'string') return;
    prefetchedRef.current = true;
    router.prefetch(href);
  };
  return (
    <Link
      ref={ref}
      href={href}
      prefetch={false}
      onMouseEnter={(e) => { prefetch(); onMouseEnter?.(e); }}
      onTouchStart={(e) => { prefetch(); onTouchStart?.(e); }}
      onFocus={(e) => { prefetch(); onFocus?.(e); }}
      {...rest}
    />
  );
});

export function useRouter(): ReturnType<typeof useNextRouter> {
  const router = useNextRouter();
  const localize = useLocalizeHref();
  return useMemo(() => ({
    ...router,
    push: (href, options) => router.push(localize(href), options),
    replace: (href, options) => router.replace(localize(href), options),
    prefetch: (href, options) => router.prefetch(localize(href), options),
  }), [router, localize]);
}

/** The app's path for the current page — without the market prefix, route named the app's way. */
export function usePathname(): string {
  const pathname = useNextPathname() || '/';
  const market = useOptionalTranslation()?.market;
  const internal = stripMarketPrefix(pathname, market);
  return toInternalRoute(internal) ?? internal;
}
